import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Card,
  Box,
  Typography,
  Grid,
  Stack,
  Tooltip,
  IconButton,
} from "@mui/material";
import {
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  AccountBalanceWalletOutlined as WalletIcon,
  PersonOutlineOutlined as CreatorIcon,
  CalendarTodayOutlined as CalendarIcon,
  MonetizationOnOutlined as CostIcon,
} from "@mui/icons-material";
import moment from "moment";
import { colors, borderRadiusPx, customShadows } from "../../../../../theme";

const shortenAddress = (addr) => {
  if (!addr) return "";
  if (addr.length <= 12) return addr;
  return `${addr.substring(0, 6)}...${addr.substring(addr.length - 4)}`;
};

const GiftCardDetailSpecs = ({
  creator = "",
  tokenName = "BET",
  tokenValue = "0",
  price = "0",
  isDonation = false,
  currency = "POL",
  expDate = 0,
}) => {
  const [copiedField, setCopiedField] = useState(null);

  const handleCopy = (text, fieldName) => {
    if (!text) return;
    try {
      navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error("Copy error:", err);
    }
  };

  const expTimestamp = Number(expDate || 0);
  const expFormatted =
    expTimestamp > 0
      ? moment.unix(expTimestamp).format("DD MMM YYYY, HH:mm")
      : "No Expiry";

  return (
    <Card
      sx={{
        p: { xs: 2.5, sm: 3.5 },
        borderRadius: borderRadiusPx.xl,
        backgroundColor: colors.dashboard.cardBg,
        border: `1px solid ${colors.dashboard.cardBorder}`,
        boxShadow: customShadows.cardFlat,
        mt: 3.5,
      }}
    >
      <Typography
        variant="h6"
        sx={{
          fontWeight: 800,
          color: colors.slate[900],
          fontSize: "1.1rem",
          mb: 2.5,
          textAlign: "left",
        }}
      >
        On-Chain Specifications
      </Typography>

      <Grid container spacing={2.5}>
        {/* Creator Spec */}
        <Grid item xs={12} sm={6}>
          <Box
            sx={{
              p: 2,
              borderRadius: borderRadiusPx.lg,
              bgcolor: colors.slate[50],
              border: `1px solid ${colors.slate[200]}`,
              height: "100%",
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <CreatorIcon sx={{ fontSize: 18, color: colors.slate[500] }} />
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: colors.slate[600],
                  textTransform: "uppercase",
                  fontSize: "0.72rem",
                }}
              >
                Card Creator
              </Typography>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  fontFamily: "monospace",
                  color: colors.slate[900],
                  fontSize: "0.875rem",
                  wordBreak: "break-all",
                }}
              >
                {shortenAddress(creator)}
              </Typography>
              <Tooltip title={copiedField === "creator" ? "Copied!" : "Copy Address"}>
                <IconButton
                  size="small"
                  onClick={() => handleCopy(creator, "creator")}
                  sx={{ p: 0.3 }}
                >
                  {copiedField === "creator" ? (
                    <CheckIcon sx={{ fontSize: 15, color: colors.emerald[600] }} />
                  ) : (
                    <CopyIcon sx={{ fontSize: 15, color: colors.slate[400] }} />
                  )}
                </IconButton>
              </Tooltip>
            </Stack>
          </Box>
        </Grid>

        {/* Value Deposit Spec */}
        <Grid item xs={12} sm={6}>
          <Box
            sx={{
              p: 2,
              borderRadius: borderRadiusPx.lg,
              bgcolor: colors.slate[50],
              border: `1px solid ${colors.slate[200]}`,
              height: "100%",
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <WalletIcon sx={{ fontSize: 18, color: colors.slate[500] }} />
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: colors.slate[600],
                  textTransform: "uppercase",
                  fontSize: "0.72rem",
                }}
              >
                Locked Token Deposit
              </Typography>
            </Stack>

            <Typography
              variant="body2"
              sx={{
                fontWeight: 800,
                color: colors.slate[900],
                fontSize: "0.95rem",
                textAlign: "left",
              }}
            >
              {tokenValue} <span style={{ color: colors.primary.main }}>{tokenName}</span>
            </Typography>
          </Box>
        </Grid>

        {/* Price / Cost Spec */}
        <Grid item xs={12} sm={6}>
          <Box
            sx={{
              p: 2,
              borderRadius: borderRadiusPx.lg,
              bgcolor: colors.slate[50],
              border: `1px solid ${colors.slate[200]}`,
              height: "100%",
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <CostIcon sx={{ fontSize: 18, color: colors.slate[500] }} />
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: colors.slate[600],
                  textTransform: "uppercase",
                  fontSize: "0.72rem",
                }}
              >
                Claim / Purchase Price
              </Typography>
            </Stack>

            <Typography
              variant="body2"
              sx={{
                fontWeight: 800,
                color: isDonation ? colors.emerald[700] : colors.slate[900],
                fontSize: "0.95rem",
                textAlign: "left",
              }}
            >
              {isDonation ? "FREE (+ Gas Fees)" : `${price} ${currency}`}
            </Typography>
          </Box>
        </Grid>

        {/* Expiry Date Spec */}
        <Grid item xs={12} sm={6}>
          <Box
            sx={{
              p: 2,
              borderRadius: borderRadiusPx.lg,
              bgcolor: colors.slate[50],
              border: `1px solid ${colors.slate[200]}`,
              height: "100%",
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <CalendarIcon sx={{ fontSize: 18, color: colors.slate[500] }} />
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: colors.slate[600],
                  textTransform: "uppercase",
                  fontSize: "0.72rem",
                }}
              >
                Expiration Date
              </Typography>
            </Stack>

            <Typography
              variant="body2"
              sx={{
                fontWeight: 800,
                color: colors.slate[900],
                fontSize: "0.95rem",
                textAlign: "left",
              }}
            >
              {expFormatted}
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </Card>
  );
};

GiftCardDetailSpecs.propTypes = {
  creator: PropTypes.string,
  tokenAddress: PropTypes.string,
  tokenName: PropTypes.string,
  tokenValue: PropTypes.string,
  price: PropTypes.string,
  isDonation: PropTypes.bool,
  currency: PropTypes.string,
  expDate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default GiftCardDetailSpecs;
